import { useMemo, useState } from 'react';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import useTag from '@/modules/corporate/modules/tags/views/TagsEditor/hooks/useTag';
import useTags from '@/modules/corporate/modules/tags/views/TagsManager/hooks/useTags';
import {
  FilterPropertyTypeEnum,
  GroupTypeEnum,
  TagAvailableOnEnum,
  TagTypeEnum,
} from '@/types/schema';

import { SystemProperty, TagAndPropertyOption } from './types';

export const getSystemProperties = (t: TFunction, type: GroupTypeEnum) =>
  getEnabledItems([
    {
      id: SystemProperty.OrgRoleId,
      name: t('Common:Filter.Designation'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.UserCollection,
    },
    {
      id: SystemProperty.DepartmentId,
      name: t('Common:Filter.Department'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.UserCollection,
    },
    {
      id: SystemProperty.ManagerId,
      name: t('Group:Editor.Manager'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.UserCollection,
    },
    {
      id: SystemProperty.IndirectManagerId,
      name: t('Group:Editor.IndirectManager'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.UserCollection,
    },
    {
      id: SystemProperty.Email,
      name: t('Group:Editor.Email'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.UserCollection,
    },
    {
      id: SystemProperty.CreatedById,
      name: t('Group:Editor.CreatedBy'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.ContentCollection,
    },
    // todo integrate after date tag
    // {
    //   id: SystemProperty.CreatedAt,
    //   name: t('Common:Filter.OrgRole'),
    //   type: FilterPropertyTypeEnum.Field,
    //   enabled: type === GroupTypeEnum.ContentCollection,
    // },
    {
      id: SystemProperty.EventType,
      name: t('Common:Filter.SessionType'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.ContentCollection,
    },
    {
      id: SystemProperty.EventDate,
      name: t('Common:Filter.SessionDate'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.ContentCollection,
    },
    {
      id: SystemProperty.Type,
      name: t('Common:Filter.TypeOfContent'),
      type: FilterPropertyTypeEnum.Field,
      enabled: type === GroupTypeEnum.ContentCollection,
    },
  ]);

const SYSTEM_PROPERTIES = [
  SystemProperty.OrgRoleId,
  SystemProperty.DepartmentId,
  SystemProperty.ManagerId,
  SystemProperty.IndirectManagerId,
  SystemProperty.CreatedById,
  SystemProperty.CreatedAt,
  SystemProperty.Type,
  SystemProperty.Email,
  SystemProperty.EventType,
  SystemProperty.EventDate,
] as string[];

const COLLECTION_TAG_AVAILABLE_ON = [TagAvailableOnEnum.Content, TagAvailableOnEnum.Event];
const USER_GROUP_TAG_AVAILABLE_ON = [TagAvailableOnEnum.User];

export default function useTagAndProperties({
  t,
  selectedProperty,
  type,
  tagTypes,
  allowedFilterTypes,
  allowedProperties,
}: {
  t: TFunction;
  selectedProperty?: string | null;
  type: GroupTypeEnum;
  tagTypes?: TagTypeEnum[];
  allowedFilterTypes: FilterPropertyTypeEnum[];
  allowedProperties?: SystemProperty[];
}) {
  const [searchText, setSearchText] = useState('');
  const { tags, fetchMore, error, isLoading } = useTags({
    filters: {
      name: searchText,
      availableOn:
        type === GroupTypeEnum.UserCollection
          ? USER_GROUP_TAG_AVAILABLE_ON
          : COLLECTION_TAG_AVAILABLE_ON,
      type: tagTypes,
    },
  });
  const systemProperties = useMemo(() => getSystemProperties(t, type), [t, type]);
  const filteredProperties: TagAndPropertyOption[] = useMemo(
    () =>
      systemProperties.filter(
        (property) =>
          (!allowedProperties || allowedProperties.includes(property.id)) &&
          property.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1,
      ),
    [systemProperties, allowedProperties, searchText],
  );

  const tagsAndProperties: TagAndPropertyOption[] = useMemo(() => {
    let values = [] as TagAndPropertyOption[];
    if (allowedFilterTypes.includes(FilterPropertyTypeEnum.Field)) {
      values = [...filteredProperties];
    }
    if (allowedFilterTypes.includes(FilterPropertyTypeEnum.Tag)) {
      tags?.forEach((tag) => {
        values.push({
          id: tag.id,
          name: tag.name,
          type: FilterPropertyTypeEnum.Tag,
          tag,
        });
      });
    }
    return values;
  }, [allowedFilterTypes, filteredProperties, tags]);

  const selectedTagAndProperty = useMemo(
    () => tagsAndProperties.find((property) => property.id === selectedProperty),
    [tagsAndProperties, selectedProperty],
  );
  const { tag: selectedTag, fetching: fetchingTags } = useTag({
    tagId: selectedProperty!,
    pause:
      !!selectedTagAndProperty ||
      !!(selectedProperty && SYSTEM_PROPERTIES.includes(selectedProperty)),
  });

  const selectedValue: TagAndPropertyOption | undefined = useMemo(() => {
    if (selectedTagAndProperty) {
      return selectedTagAndProperty;
    }
    if (selectedTag) {
      return {
        id: selectedTag.id,
        name: selectedTag.name,
        type: FilterPropertyTypeEnum.Tag,
        tag: selectedTag,
      };
    }
    return undefined;
  }, [selectedTagAndProperty, selectedTag]);

  return {
    searchText,
    setSearchText,
    tagsAndProperties,
    fetchingSelected: fetchingTags,
    fetchMore,
    error,
    selectedValue,
    initialFetching: isLoading,
  };
}
